import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Table, InputNumber, message, Alert, Space, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;

const EmployeeSalaryForm = ({ 
  visible, 
  onCancel, 
  onSubmit, 
  employee = null,
  templates = [],
  salaryItems = [],
  initialData = null 
}) => {
  const [form] = Form.useForm();
  const [salaryItemsList, setSalaryItemsList] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [notes, setNotes] = useState('');

  // 监听薪资项目列表变化
  useEffect(() => {
    console.log('📋 薪资项目列表已更新:', salaryItemsList);
  }, [salaryItemsList]);

  useEffect(() => {
    if (visible) {
      console.log('EmployeeSalaryForm props:', { templates, initialData });
      if (initialData) {
        // 编辑模式 - 显示当前薪资数据，模板选择为空（可以随时选择模板快速填充）
        console.log('编辑模式 - initialData:', initialData);
        form.setFieldsValue({
          template_id: undefined, // 模板选择始终为空，用户可以随时选择
          effective_date: initialData.effective_date,
          notes: initialData.notes
        });
        setEffectiveDate(initialData.effective_date || '');
        setNotes(initialData.notes || '');
        console.log('📋 加载的薪资项目数据:', initialData.items);
        console.log('📋 项目详情:', initialData.items?.map(item => ({
          salary_item_name: item.salary_item_name,
          value: item.value,
          amount: item.amount,
          percentage: item.percentage,
          calculation_type: item.calculation_type
        })));
        
        // 处理加载的薪资项目，确保每个项目都有必要的字段和ID
        const processedItems = (initialData.items || []).map((item, index) => {
          console.log(`🔍 处理项目 ${index}:`, {
            name: item.salary_item_name,
            原始value: item.value,
            原始amount: item.amount,
            原始percentage: item.percentage,
            calculation_type: item.calculation_type
          });
          
          // 根据计算类型确定value字段的值
          let value = parseFloat(item.value) || 0;
          
          // 如果value为0，尝试从对应字段获取
          if (value === 0) {
            if (item.calculation_type === 'percentage') {
              value = parseFloat(item.percentage) || 0;
            } else if (item.calculation_type === 'fixed') {
              value = parseFloat(item.amount) || 0;
            }
          }
          
          console.log(`✅ 项目 ${index} 处理后value:`, value);
          
          return {
            ...item,
            id: item.id || Date.now() + index, // 确保每个项目有唯一ID
            value: value, // 确保value字段有值
            amount: parseFloat(item.amount) || 0,
            percentage: parseFloat(item.percentage) || 0,
            calculated_amount: parseFloat(item.calculated_amount) || 0
          };
        });
        
        console.log('📋 处理后的薪资项目:', processedItems);
        setSalaryItemsList(processedItems);
        setSelectedTemplate(null); // 模板选择为空
      } else {
        // 新建模式 - 显示空表单，用户可以选择模板快速填充
        console.log('新建模式');
        form.resetFields();
        setSalaryItemsList([]);
        setSelectedTemplate(null);
        setEffectiveDate('');
        setNotes('');
      }
    }
  }, [visible, initialData, form, templates]);

  // 编辑模式下不再需要关联模板，模板只用于首次创建时的快速初始化

  const handleTemplateChange = async (templateId) => {
    if (!templateId) {
      setSelectedTemplate(null);
      return;
    }

    // 如果当前已有薪资项目，询问用户是否清空
    if (salaryItemsList.length > 0) {
      Modal.confirm({
        title: '应用薪资模板',
        content: '当前已有薪资项目配置，是否清空现有配置后应用新模板？',
        okText: '清空并应用',
        cancelText: '追加到现有配置',
        onOk: () => applyTemplateToSalary(templateId, true), // 清空后应用
        onCancel: () => applyTemplateToSalary(templateId, false), // 追加应用
      });
    } else {
      // 没有现有配置，直接应用
      applyTemplateToSalary(templateId, true);
    }
  };

  const applyTemplateToSalary = async (templateId, clearExisting) => {
    try {
      const response = await fetch(`/api/salary/position-templates/${templateId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        const template = result.data;
        console.log('📋 获取到的模板数据:', template);
        console.log('📋 模板项目数据:', template.items);
        setSelectedTemplate(template);
        
        // 应用模板项目
        const templateItems = template.items.map((item, index) => {
          console.log(`🔍 模板项目 ${index}:`, {
            name: item.salary_item_name,
            amount: item.amount,
            value: item.value,
            percentage: item.percentage,
            calculation_type: item.calculation_type
          });
          
          // 优先使用value字段（这是统一字段），如果没有再根据类型取对应字段
          let value = parseFloat(item.value) || 0;
          let amount = parseFloat(item.amount) || 0;
          let percentage = parseFloat(item.percentage) || 0;
          
          // 如果value为0，根据计算类型从对应字段获取
          if (value === 0) {
            if (item.calculation_type === 'percentage') {
              value = percentage;
            } else if (item.calculation_type === 'fixed') {
              value = amount;
            }
          }
          
          let calculatedAmount = 0;
          if (item.calculation_type === 'fixed') {
            calculatedAmount = value;
          } else if (item.calculation_type === 'percentage') {
            calculatedAmount = 0; // 百分比需要基数才能计算实际金额
          } else if (item.calculation_type === 'formula') {
            calculatedAmount = 0; // 公式需要解析才能计算
          }
          
          console.log(`✅ 模板项目 ${index} 处理后:`, { value, amount, percentage, calculatedAmount });
          
          return {
            id: Date.now() + index,
            salary_item_id: item.salary_item_id || item.id,
            salary_item_name: item.salary_item_name || '',
            salary_item_type: item.salary_item_type || 'base',
            calculation_type: item.calculation_type || 'fixed',
            amount: amount,
            percentage: percentage,
            value: value, // 统一的值字段
            formula: item.formula || '',
            description: item.description || '',
            calculated_amount: calculatedAmount,
            is_custom: false,
            sort_order: item.sort_order || index
          };
        });
        
        console.log('📋 转换后的模板项目:', templateItems);
        
        if (clearExisting) {
          // 清空现有配置后应用模板
          setSalaryItemsList(templateItems);
          console.log('📋 设置薪资项目列表:', templateItems);
          message.success('已清空现有配置并应用薪资模板');
        } else {
          // 追加到现有配置
          const currentItems = [...salaryItemsList];
          const maxSortOrder = Math.max(0, ...currentItems.map(item => item.sort_order || 0));
          
          const appendedItems = templateItems.map((item, index) => ({
            ...item,
            id: Date.now() + index + 1000, // 避免ID冲突
            sort_order: maxSortOrder + index + 1
          }));
          
          setSalaryItemsList([...currentItems, ...appendedItems]);
          message.success('已将模板项目追加到现有配置');
        }
        
        // 如果是新建模式且还没有设置生效日期，自动设置为今天
        if (!initialData && !effectiveDate) {
          const today = new Date().toISOString().split('T')[0];
          setEffectiveDate(today);
          form.setFieldsValue({ effective_date: today });
        }
      }
    } catch (error) {
      console.error('获取模板详情失败:', error);
      message.error('获取模板详情失败');
    }
  };

  const handleAddCustomItem = () => {
    const newItem = {
      id: Date.now(),
      salary_item_name: '',
      salary_item_type: 'base',
      calculation_type: 'fixed',
      amount: 0,
      percentage: 0,
      value: 0, // 统一的值字段
      formula: '',
      description: '', // 添加说明字段
      calculated_amount: 0,
      is_custom: true,
      sort_order: salaryItemsList.length
    };
    setSalaryItemsList([...salaryItemsList, newItem]);
    
    // 如果是新建模式且还没有设置生效日期，自动设置为今天
    if (!initialData && !effectiveDate) {
      const today = new Date().toISOString().split('T')[0];
      setEffectiveDate(today);
      form.setFieldsValue({ effective_date: today });
    }
  };

  const handleRemoveItem = (index) => {
    const newItems = salaryItemsList.filter((_, i) => i !== index);
    setSalaryItemsList(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...salaryItemsList];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // 重新计算金额，确保是数字类型
    if (field === 'value' || field === 'amount' || field === 'percentage' || field === 'calculation_type') {
      const item = newItems[index];
      if (item.calculation_type === 'percentage') {
        // 百分比类型：显示百分比值，计算金额为0（需要基数才能计算）
        newItems[index].calculated_amount = 0; // 实际计算需要基数
        // 同时更新percentage字段以保持兼容性
        if (field === 'value') {
          newItems[index].percentage = value;
        }
      } else if (item.calculation_type === 'formula') {
        // 公式类型：计算金额为0（需要解析公式）
        newItems[index].calculated_amount = 0;
      } else {
        // 固定金额类型
        const amount = item.value || item.amount || 0;
        newItems[index].calculated_amount = parseFloat(amount) || 0;
        // 同时更新amount字段以保持兼容性
        if (field === 'value') {
          newItems[index].amount = value;
        }
      }
    }
    
    setSalaryItemsList(newItems);
  };

  const calculateTotalSalary = () => {
    return salaryItemsList.reduce((total, item) => {
      let amount = 0;
      
      // 根据计算类型获取正确的值
      if (item.calculation_type === 'percentage') {
        // 百分比类型：不直接加到总金额中，因为需要基于业绩计算
        // 百分比项目在薪资计算中需要基于实际业绩来计算，这里不参与固定薪资合计
        amount = 0;
      } else if (item.calculation_type === 'formula') {
        // 公式类型：需要解析公式计算
        amount = 0; // 暂时为0，需要实现公式解析
      } else {
        // 固定金额类型
        amount = parseFloat(item.calculated_amount || item.value || item.amount || 0) || 0;
      }
      
      if (item.salary_item_type === 'deduction') {
        return total - amount;
      } else {
        return total + amount;
      }
    }, 0);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      const filteredItems = salaryItemsList.filter(item => item.salary_item_name && item.salary_item_name.trim());
      
      console.log('💾 准备提交薪资数据');
      console.log('📋 模式:', initialData ? '编辑模式' : '新建模式');
      console.log('📋 原始薪资项目列表:', salaryItemsList);
      console.log('📋 过滤后的薪资项目:', filteredItems);
      console.log('📋 项目详情:', filteredItems.map(item => ({
        salary_item_id: item.salary_item_id,
        salary_item_name: item.salary_item_name,
        amount: item.amount,
        value: item.value
      })));
      
      // 后端只需要薪资结构数据，不需要模板信息
      // 模板只是前端快速填充的工具
      const submitData = {
        effective_date: effectiveDate,
        notes: notes,
        items: filteredItems,
        total_salary: calculateTotalSalary()
      };
      
      console.log('📤 提交数据（只包含薪资结构）:', submitData);
      
      await onSubmit(submitData);
      message.success(initialData ? '员工薪资更新成功' : '员工薪资创建成功');
      onCancel();
    } catch (error) {
      console.error('❌ 提交失败:', error);
      message.error('提交失败，请重试');
    } finally {
      setLoading(false);
    }
  };


  const totalSalary = calculateTotalSalary();

  const columns = [
    {
      title: '薪资项目名称',
      key: 'salary_item_name',
      width: 180,
      render: (_, record, index) => (
        <Input
          value={record.salary_item_name}
          onChange={(e) => handleItemChange(index, 'salary_item_name', e.target.value)}
          placeholder="输入薪资项目名称"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '类型',
      key: 'salary_item_type',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.salary_item_type}
          onChange={(value) => handleItemChange(index, 'salary_item_type', value)}
          style={{ width: '100%' }}
        >
          <Option value="base">基本工资</Option>
          <Option value="allowance">津贴</Option>
          <Option value="bonus">奖金</Option>
          <Option value="deduction">扣除</Option>
        </Select>
      ),
    },
    {
      title: '计算方式',
      key: 'calculation_type',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.calculation_type}
          onChange={(value) => handleItemChange(index, 'calculation_type', value)}
          style={{ width: '100%' }}
        >
          <Option value="fixed">固定金额</Option>
          <Option value="percentage">百分比</Option>
          <Option value="formula">公式计算</Option>
        </Select>
      ),
    },
    {
      title: '值',
      key: 'value',
      width: 120,
      render: (_, record, index) => {
        if (record.calculation_type === 'percentage') {
          return (
            <InputNumber
              value={parseFloat(record.value) || parseFloat(record.percentage) || 0}
              onChange={(value) => handleItemChange(index, 'value', value || 0)}
              placeholder="0"
              min={0}
              max={100}
              precision={2}
              style={{ width: '100%' }}
              formatter={value => `${value}%`}
              parser={value => value.replace('%', '')}
            />
          );
        } else if (record.calculation_type === 'formula') {
          return (
            <Input
              value={record.formula}
              onChange={(e) => handleItemChange(index, 'formula', e.target.value)}
              placeholder="输入计算公式"
              style={{ width: '100%' }}
            />
          );
        } else {
          return (
            <InputNumber
              value={record.value || record.amount}
              onChange={(value) => handleItemChange(index, 'value', value || 0)}
              placeholder="0"
              min={0}
              precision={2}
              style={{ width: '100%' }}
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/¥\s?|(,*)/g, '')}
            />
          );
        }
      },
    },
    {
      title: '说明',
      key: 'description',
      width: 150,
      render: (_, record, index) => (
        <Input
          value={record.description}
          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
          placeholder="项目说明"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '来源',
      key: 'source',
      width: 80,
      render: (_, record) => (
        <span style={{ 
          color: record.is_custom ? '#fa8c16' : '#52c41a',
          fontSize: '12px'
        }}>
          {record.is_custom ? '个人定制' : '模板'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(index)}
          size="small"
        />
      ),
    },
  ];

  // 检查员工是否已离职
  const isEmployeeResigned = employee && (employee.status === 'resigned' || employee.status === 'terminated' || employee.status === 'resigning');

  return (
    <Modal
      title={
        <div>
          {initialData ? '编辑员工薪资' : '设置员工薪资'}
          {employee && (
            <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
              - {employee.name} ({employee.employee_no})
            </span>
          )}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={loading} 
          onClick={handleSubmit}
          disabled={isEmployeeResigned}
        >
          {initialData ? '更新' : '创建'}
        </Button>,
      ]}
    >
      {employee && (
        <Alert
          message={
            <div>
              <strong>员工信息：</strong>
              {employee.name} ({employee.employee_no}) - {employee.position_name || '未设置岗位'} - {employee.department_name || '未设置部门'}
            </div>
          }
          type="info"
          style={{ marginBottom: '16px' }}
        />
      )}

      {isEmployeeResigned && (
        <Alert
          message="该员工已离职，无法编辑薪资信息"
          description="离职员工的薪资信息已被锁定，如需修改请联系系统管理员。"
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        disabled={isEmployeeResigned}
      >
        {/* 第一行：薪资模板和生效时间 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', marginBottom: '16px' }}>
          {/* 模板选择 - 新建和编辑时都可以选择，用于快速填充薪资项 */}
          <Form.Item name="template_id" label="薪资模板（可选 - 快速填充工具）">
            <Select
              placeholder="选择薪资模板快速填充薪资项和金额"
              allowClear
              onChange={handleTemplateChange}
              disabled={loading}
            >
              {templates.map(template => (
                <Option key={template.id} value={template.id}>
                  {template.name} (v{template.version})
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* 生效日期 */}
          {(selectedTemplate || initialData || salaryItemsList.length > 0) && (
            <Form.Item
              name="effective_date"
              label="生效日期"
              rules={[{ required: true, message: '请选择生效日期' }]}
            >
              <Input 
                type="date" 
                onChange={(e) => {
                  setEffectiveDate(e.target.value);
                  form.setFieldValue('effective_date', e.target.value);
                }}
              />
            </Form.Item>
          )}
        </div>

        {/* 薪资模板信息显示 */}
        {selectedTemplate && (
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
              <span style={{ fontWeight: '600', color: '#1890ff' }}>📋 选用模板：{selectedTemplate.name}</span>
              <span style={{ fontSize: '12px', color: '#666' }}>版本 v{selectedTemplate.version}</span>
            </div>
            {selectedTemplate.description && (
              <div style={{ fontSize: '14px', color: '#666' }}>
                <span style={{ fontWeight: '500' }}>模板说明：</span>
                {selectedTemplate.description}
              </div>
            )}
          </div>
        )}

        {/* 第二行：调整备注 */}
        {(() => {
          const shouldShow = selectedTemplate || initialData || salaryItemsList.length > 0;
          return shouldShow;
        })() && (
          <div style={{ marginBottom: '16px' }}>
            <Form.Item 
              name="notes" 
              label={
                <span>
                  调整备注
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>
                    (此信息将记录到员工的跟进记录中)
                  </span>
                </span>
              }
            >
              <Input.TextArea 
                rows={3} 
                placeholder="请输入本次薪资调整的备注说明，例如：年度调薪、晋升调薪、绩效调薪等"
                onChange={(e) => {
                  setNotes(e.target.value);
                  form.setFieldValue('notes', e.target.value);
                }}
              />
            </Form.Item>
          </div>
        )}
      </Form>

      <Divider />

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h4 style={{ margin: 0 }}>薪资项目明细</h4>
          <Space>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: totalSalary >= 0 ? '#52c41a' : '#f5222d' }}>
              合计：¥{totalSalary.toLocaleString()}
            </div>
            <Button 
              type="dashed" 
              icon={<PlusOutlined />} 
              onClick={handleAddCustomItem}
              size="small"
            >
              添加个人项目
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={salaryItemsList}
          pagination={false}
          size="small"
          rowKey={(record, index) => record.id || index}
          locale={{ emptyText: '暂无薪资项目，请选择薪资模板或添加个人项目' }}
          scroll={{ y: 300 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <strong>合计</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <strong style={{ color: totalSalary >= 0 ? '#52c41a' : '#f5222d' }}>
                    ¥{totalSalary.toLocaleString()}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} colSpan={2} />
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </div>
    </Modal>
  );
};

export default EmployeeSalaryForm;
